import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/layout/Sidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Edit, 
  Trash2, 
  Mail, 
  Shield, 
  Building2, 
  Check, 
  X,
  RefreshCw,
  Users,
  Eye,
  EyeOff,
  ArrowLeft,
  LayoutDashboard
} from 'lucide-react';
import api from '../lib/api';

const RESTAURANTS = [
  { id: "9d5ba3d3-838c-4995-affd-f05e7a83c40a", name: "Ma biche sur le toit" },
  { id: "b201bde3-d966-4c9e-858b-a5c58a5bd672", name: "Compagnie Française" },
  { id: "f6cb176c-6ef1-4857-95c3-b9c017c49245", name: "Coup de bambou" },
  { id: "661527fe-9265-40d3-87df-291faacaf708", name: "Jacques Balma" },
  { id: "ace84b06-4560-4aac-b031-5f6ba1c01d3c", name: "Compagnie Busca" },
  { id: "0b36db26-e6bc-41b5-9a83-a4055503c6cf", name: "Café Chouchou" },
  { id: "ac13dbb1-ebb1-4029-a388-3ecb8db825a1", name: "Sous les pins" },
  { id: "b169bf61-2e65-4361-844e-653e2156771d", name: "Jacques Blagnac" },
];

const ROLES = [
  { value: "admin", label: "Administrateur", description: "Accès complet à toutes les fonctionnalités" },
  { value: "direction", label: "Direction", description: "Accès à tous les restaurants et au consolidé" },
  { value: "directeur", label: "Directeur", description: "Accès aux restaurants assignés uniquement" },
];

const AdminPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Formulaire
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'directeur',
    restaurant_ids: [],
    has_consolidated_access: false,
    password: '',
    send_invite: true
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.data || []);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.email || !formData.name) {
      toast.error("Email et nom requis");
      return;
    }

    setSendingInvite(true);
    try {
      let response;
      
      if (formData.send_invite) {
        // Création avec envoi d'email d'invitation
        response = await api.post('/admin/users/invite', {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          restaurant_ids: formData.restaurant_ids,
          has_consolidated_access: formData.has_consolidated_access
        });
        
        if (response.data.email_sent) {
          toast.success(`Invitation envoyée à ${formData.email}`);
        } else {
          toast.warning("Utilisateur créé mais l'email n'a pas pu être envoyé");
        }
      } else {
        // Création directe avec mot de passe
        if (!formData.password || formData.password.length < 6) {
          toast.error("Le mot de passe doit faire au moins 6 caractères");
          setSendingInvite(false);
          return;
        }
        
        response = await api.post('/admin/users', {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          restaurant_ids: formData.restaurant_ids,
          has_consolidated_access: formData.has_consolidated_access,
          password: formData.password
        });
        
        toast.success("Utilisateur créé avec succès");
      }
      
      setShowCreateDialog(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      await api.put(`/admin/users/${selectedUser.id}`, {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        restaurant_ids: formData.restaurant_ids,
        has_consolidated_access: formData.has_consolidated_access
      });
      
      toast.success("Utilisateur mis à jour");
      setShowEditDialog(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Êtes-vous sûr de vouloir désactiver cet utilisateur ?")) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Utilisateur désactivé");
      loadUsers();
    } catch (error) {
      toast.error("Erreur lors de la désactivation");
    }
  };

  const handleResendInvite = async (user) => {
    try {
      const response = await api.post(`/admin/users/${user.id}/resend-invite`);
      if (response.data.email_sent) {
        toast.success(`Email renvoyé à ${user.email}`);
      } else {
        toast.error("Erreur lors de l'envoi de l'email");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      role: 'directeur',
      restaurant_ids: [],
      has_consolidated_access: false,
      password: '',
      send_invite: true
    });
    setSelectedUser(null);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      restaurant_ids: user.restaurant_ids || [],
      has_consolidated_access: user.has_consolidated_access || false,
      password: '',
      send_invite: false
    });
    setShowEditDialog(true);
  };

  const toggleRestaurant = (restaurantId) => {
    setFormData(prev => ({
      ...prev,
      restaurant_ids: prev.restaurant_ids.includes(restaurantId)
        ? prev.restaurant_ids.filter(id => id !== restaurantId)
        : [...prev.restaurant_ids, restaurantId]
    }));
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700 border-red-200';
      case 'direction': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const getStatusBadge = (user) => {
    if (!user.is_active) {
      return <Badge variant="outline" className="bg-gray-100 text-gray-600">Inactif</Badge>;
    }
    if (!user.password_set) {
      return <Badge variant="outline" className="bg-amber-100 text-amber-700">En attente</Badge>;
    }
    return <Badge variant="outline" className="bg-green-100 text-green-700">Actif</Badge>;
  };

  return (
    <div className="flex h-screen bg-background" data-testid="admin-page">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="gap-2"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour au Dashboard
            </Button>
            <div>
              <h1 className="text-xl font-heading font-semibold text-foreground flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Administration
              </h1>
              <p className="text-sm text-muted-foreground">Gestion des accès utilisateurs</p>
            </div>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2"
                data-testid="create-user-btn"
                onClick={() => { resetForm(); setShowCreateDialog(true); }}
              >
                <UserPlus className="h-4 w-4" />
                Nouvel utilisateur
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border text-foreground max-w-lg">
              <DialogHeader>
                <DialogTitle>Créer un utilisateur</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  L'utilisateur recevra un email pour créer son mot de passe
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom complet</Label>
                    <Input
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      placeholder="Jean Dupont"
                      className="bg-background border-border"
                      data-testid="user-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({...formData, email: e.target.value})}
                      placeholder="jean@exemple.fr"
                      className="bg-background border-border"
                      data-testid="user-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Rôle</Label>
                  <Select value={formData.role} onValueChange={v => setFormData({...formData, role: v})}>
                    <SelectTrigger className="bg-background border-border" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {ROLES.map(role => (
                        <SelectItem key={role.value} value={role.value}>
                          <div>
                            <div className="font-medium">{role.label}</div>
                            <div className="text-xs text-muted-foreground">{role.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.role === 'directeur' && (
                  <div>
                    <Label className="mb-2 block">Restaurants accessibles</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {RESTAURANTS.map(restaurant => (
                        <div 
                          key={restaurant.id}
                          className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            formData.restaurant_ids.includes(restaurant.id)
                              ? 'bg-primary/10 border-primary'
                              : 'bg-background border-border hover:border-muted-foreground'
                          }`}
                          onClick={() => toggleRestaurant(restaurant.id)}
                        >
                          <Checkbox 
                            checked={formData.restaurant_ids.includes(restaurant.id)}
                            className="data-[state=checked]:bg-primary"
                          />
                          <span className="text-sm">{restaurant.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2 p-3 rounded bg-muted/50 border border-border">
                  <Checkbox 
                    checked={formData.has_consolidated_access}
                    onCheckedChange={v => setFormData({...formData, has_consolidated_access: v})}
                    className="data-[state=checked]:bg-primary"
                    data-testid="consolidated-access-checkbox"
                  />
                  <div>
                    <Label className="cursor-pointer">Accès vue consolidée</Label>
                    <p className="text-xs text-muted-foreground">Permet de voir les données de tous les restaurants</p>
                  </div>
                </div>
                
                <div className="border-t border-border pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Checkbox 
                      checked={formData.send_invite}
                      onCheckedChange={v => setFormData({...formData, send_invite: v})}
                      className="data-[state=checked]:bg-primary"
                    />
                    <Label>Envoyer un email d'invitation</Label>
                  </div>
                  
                  {!formData.send_invite && (
                    <div>
                      <Label>Mot de passe</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={e => setFormData({...formData, password: e.target.value})}
                          placeholder="Minimum 6 caractères"
                          className="bg-background border-border pr-10"
                          data-testid="user-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleCreateUser}
                  disabled={sendingInvite}
                  data-testid="confirm-create-btn"
                >
                  {sendingInvite ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {formData.send_invite ? "Envoi en cours..." : "Création..."}
                    </>
                  ) : (
                    <>
                      {formData.send_invite ? <Mail className="h-4 w-4 mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                      {formData.send_invite ? "Envoyer l'invitation" : "Créer"}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{users.length}</p>
                      <p className="text-sm text-muted-foreground">Utilisateurs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-red-100">
                      <Shield className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
                      <p className="text-sm text-muted-foreground">Admins</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-green-100">
                      <Check className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{users.filter(u => u.is_active && u.password_set !== false).length}</p>
                      <p className="text-sm text-muted-foreground">Actifs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-amber-100">
                      <Mail className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{users.filter(u => u.password_set === false).length}</p>
                      <p className="text-sm text-muted-foreground">En attente</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Users Table */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Liste des utilisateurs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary" />
                    <p className="mt-2 text-muted-foreground">Chargement...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="users-table">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Utilisateur</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Rôle</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Restaurants</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Statut</th>
                          <th className="text-right py-3 px-4 text-muted-foreground font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(user => (
                          <tr key={user.id} className="border-b border-border/50 hover:bg-muted/50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium">{user.name}</p>
                                <p className="text-sm text-muted-foreground">{user.email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={getRoleBadgeColor(user.role)}>
                                {ROLES.find(r => r.value === user.role)?.label || user.role}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              {user.role === 'admin' || user.role === 'direction' ? (
                                <span className="text-sm text-muted-foreground">Tous les restaurants</span>
                              ) : user.restaurant_ids?.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {user.restaurant_ids.slice(0, 2).map(id => (
                                    <Badge key={`${user.id}-${id}`} variant="outline" className="text-xs">
                                      {RESTAURANTS.find(r => r.id === id)?.name || id}
                                    </Badge>
                                  ))}
                                  {user.restaurant_ids.length > 2 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{user.restaurant_ids.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">Aucun</span>
                              )}
                              {user.has_consolidated_access && (
                                <Badge variant="outline" className="ml-1 text-xs bg-purple-100 text-purple-700">
                                  Consolidé
                                </Badge>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {getStatusBadge(user)}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-2">
                                {user.password_set === false && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleResendInvite(user)}
                                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                    data-testid={`resend-invite-${user.id}`}
                                  >
                                    <Mail className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditDialog(user)}
                                  data-testid={`edit-user-${user.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-card border-border text-foreground max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier l'utilisateur</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom complet</Label>
                  <Input
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    className="bg-background border-border"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="bg-background border-border"
                  />
                </div>
              </div>
              
              <div>
                <Label>Rôle</Label>
                <Select value={formData.role} onValueChange={v => setFormData({...formData, role: v})}>
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.role === 'directeur' && (
                <div>
                  <Label className="mb-2 block">Restaurants accessibles</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {RESTAURANTS.map(restaurant => (
                      <div 
                        key={restaurant.id}
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                          formData.restaurant_ids.includes(restaurant.id)
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background border-border hover:border-muted-foreground'
                        }`}
                        onClick={() => toggleRestaurant(restaurant.id)}
                      >
                        <Checkbox 
                          checked={formData.restaurant_ids.includes(restaurant.id)}
                          className="data-[state=checked]:bg-primary"
                        />
                        <span className="text-sm">{restaurant.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-2 p-3 rounded bg-muted/50 border border-border">
                <Checkbox 
                  checked={formData.has_consolidated_access}
                  onCheckedChange={v => setFormData({...formData, has_consolidated_access: v})}
                  className="data-[state=checked]:bg-primary"
                />
                <div>
                  <Label className="cursor-pointer">Accès vue consolidée</Label>
                  <p className="text-xs text-muted-foreground">Permet de voir les données de tous les restaurants</p>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Annuler
              </Button>
              <Button onClick={handleUpdateUser}>
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPage;
